import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

// CSS
import "./clientPicker.css";

// Components
import SearchBar from "../Other Components/searchBar";
import ItemCard from "../Item Components/itemCard";
import Button from "../Other Components/button";
import PageLoader from "../Other Components/pageLoader";
import Title from "../Other Components/Title";

// Functions and hooks
import { GetClients } from "../../BackendAccess/ClientHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { useTranslation } from "react-i18next";

const DEFAULT_TYPES_TO_SHOW = ["Buyer"]; //Defined to prevent rerenders and infinite loops

export default function ClientPicker({
  title = "Clients",
  onChange,
  typesToShow = DEFAULT_TYPES_TO_SHOW,
  PickedClients,
  singleSelect = false,
  disabled,
  className,
}) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);

  const [localTypesToShow, setLocalTypesToShow] = useState([]);

  const finalClassName = twMerge("client-picker w-100 h-100", className);

  const [loading, setLoading] = useState(false); // Add loading state
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const containerRef = useRef(null);

  const { showNotification } = useNotification();

  useErrorHandler(error, showNotification, setPageError);

  const { t } = useTranslation();

  useEffect(() => {
    if (
      typesToShow.length !== localTypesToShow.length ||
      !typesToShow.every((val, index) => val === localTypesToShow[index])
    ) {
      GetAllClients();
      setLocalTypesToShow(typesToShow);
      if (PickedClients.length > 0) setSelectedClients(PickedClients);
    }
  }, [typesToShow]);

  useEffect(() => {
    onChange(selectedClients);
  }, [selectedClients]);

  const GetAllClients = async () => {
    setLoading(true);
    try {
      const [data, error] = await GetClients();
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        const filteredData = data.filter((item) => {
          return [...typesToShow].includes(item.Type);
        });
        setClients(filteredData);
      }
    } catch (error) {
      setError({
        err: t("server.Server error", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (term) => {
    if (!disabled) setSearchTerm(term);
  };

  const handleSearchFocus = () => {
    if (!disabled) setSearching(true);
  };

  const handleSearchBlur = (e) => {
    if (!containerRef.current.contains(e.relatedTarget) && !disabled) {
      setSearching(false);
    }
  };

  const handleClickOutside = (e) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target) &&
      !disabled
    ) {
      setSearching(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const filteredClients = clients.filter((item) => {
    const lowerCaseTerm = searchTerm.toLowerCase();
    return (
      item.Name.toLowerCase().includes(lowerCaseTerm) ||
      item.ClientID.toString().toLowerCase().includes(lowerCaseTerm)
    );
  });

  const sortedClients = [...filteredClients].sort((a, b) =>
    a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1
  );

  const onClientSelectChanged = (id, state) => {
    if (!disabled) {
      var newSelectedIds = [...selectedClients];
      if (state) {
        if (!singleSelect || newSelectedIds.length === 0) {
          newSelectedIds.push(id);
        } else if (newSelectedIds.length !== 0 && singleSelect) {
          newSelectedIds = [];
          newSelectedIds.push(id);
        }
      } else {
        newSelectedIds = newSelectedIds.filter((item) => item !== id);
      }
      setSelectedClients(newSelectedIds);
    }
  };

  const handleCreateClick = async () => {
    if (!disabled) navigate("/workspace/clients/clientCreationPage");
  };
  const handleSecondAction = async (id) => {
    if (!disabled)
      navigate("/workspace/clients/clientInfoPage", { state: { id } });
  };

  const createCardProps = (item) => {
    return [
      { Name: "ID", Value: item.ClientID, columnWidth: 1 },
      { Name: "Name", Value: item.Name, columnWidth: 3 },
    ];
  };

  return (
    <div className={finalClassName} ref={containerRef} tabIndex={-1}>
      {disabled ? <div className="disabledDimmingCover"></div> : null}
      <Title className="h-auto w-100 font-l mb-1">{title}</Title>
      <div className="client-picker-content">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          shadow={false}
          placeholder={t("Search clients...", { ns: "clients" })}
        />

        <div className="client-picker-lists-container">
          <PageLoader loading={loading} error={pageError}>
            <ul
              className={
                searching
                  ? "client-picker-list"
                  : "client-picker-list collapsed"
              }
            >
              {sortedClients.map((item) => (
                <li
                  key={item.ClientID}
                  className="client-picker-list-item"
                  onClick={handleSearchFocus}
                >
                  <ItemCard
                    key={item.ClientID}
                    ItemID={item.ClientID}
                    propertiesToShow={createCardProps(item)}
                    bBtnVisible={false}
                    bBodyClickable={true}
                    bBtnSectionVisible={false}
                    bSelectable={true}
                    onSelected={() => {}}
                    selectedItemIds={selectedClients}
                    setIDSelected={onClientSelectChanged}
                    onSecondAction={() => handleSecondAction(item.ClientID)}
                  />
                </li>
              ))}
            </ul>

            <ul className="picked-clients-list">
              {!loading && selectedClients.length > 0 ? (
                selectedClients.map((clientId) => {
                  const client = clients.find((cl) => cl.ClientID === clientId);
                  return client ? (
                    <li key={clientId} className="picked-clients-list-item">
                      <ItemCard
                        key={client.ClientID}
                        ItemID={client.ClientID}
                        propertiesToShow={createCardProps(client)}
                        bBtnVisible={false}
                        bBodyClickable={true}
                        bBtnSectionVisible={false}
                        bSelectable={true}
                        onSelected={() => {}}
                        selectedItemIds={selectedClients}
                        setIDSelected={onClientSelectChanged}
                      />
                    </li>
                  ) : null;
                })
              ) : (
                <p className="cardPlaceholder Client">
                  {t("No selected clients", { ns: "clients" })}
                </p> // Placeholder if no clients are selected
              )}
            </ul>
            <Button
              btnSize={"btn-normal"}
              btnStyle={"btn-positive"}
              className=" w-90 mb-2"
              btnMargin={"btn-default"}
              onClick={handleCreateClick}
              bNoShadow={true}
              icon={"create"}
            >
              {t("Create Client", { ns: "clients" })}
            </Button>
          </PageLoader>
        </div>
      </div>
    </div>
  );
}
